import cookie from 'js-cookie';
import type { OS } from 'types';
import type { User } from 'types/User';
import * as uuid from 'uuid';

import braze from '~/services/braze.client';
import facebook from '~/services/facebook';
import facebookPixel from '~/services/facebook.pixel';
import gtm from '~/services/gtm.client';
import tiktokServer from '~/services/tiktok';
import tiktok from '~/services/tiktok.pixel';
import twitter from '~/services/twitter.pixel';
import { sha256 } from '~/utils/hash';
import { getPreviousPagePosition } from '~/utils/navigationContext';

import UserEvent from './UserEvent';

type EventData = {
  clientIp: string | null;
  isBot: boolean;
  legacyId: string;
  os: OS;
  pagePosition: string;
  productName: string;
  user: User;
};

class SetNotificationEvent extends UserEvent<EventData> {
  async sendToTikTok() {
    const eventId = uuid.v4();
    const hashedProfile = await tiktokServer.getHashedProfile(
      this.data.user.profile
    );

    this.serverEvents.push({
      data: [
        {
          context: {
            ip: this.data.clientIp ?? '',
            page: { url: window.location.href },
            user_agent: navigator?.userAgent ?? ''
          },
          event: 'NotifyMeSet',
          event_id: eventId,
          event_time: Math.floor(Date.now() / 1000),
          user: {
            external_id: this.data.user.id ?? 'guest',
            ttclid: cookie.get('tss_ttclid'),
            ttp: cookie.get('_ttp'),
            ...hashedProfile
          }
        }
      ],
      type: 'tiktok'
    });

    tiktok.fireEvent('NotifyMeSet', undefined, eventId);
  }

  async sendToFacebook() {
    const eventId = uuid.v4();

    await facebookPixel.fireCustomEvent(
      facebookPixel.CustomEventNames.NotifyMeSet,
      {},
      eventId
    );

    const hashedProfile = await facebook.getHashedProfile(
      this.data.user.profile
    );

    const externalId = await sha256(this.data.user.id ?? 'guest');

    const serverEventData = [
      {
        action_source: 'website',
        event_id: eventId,
        event_name: facebookPixel.CustomEventNames.NotifyMeSet,
        event_source_url: window.location.href,
        event_time: Math.floor(Date.now() / 1000),
        user_data: {
          client_ip_address: this.data.clientIp,
          client_user_agent: navigator.userAgent,
          external_id: externalId,
          fbc: cookie.get('_fbc'),
          fbp: cookie.get('_fbp'),
          ...hashedProfile
        }
      }
    ];

    this.serverEvents.push({ data: serverEventData, type: 'facebook' });
  }

  sendToTwitter() {
    return twitter.fireEvent(twitter.EventIds.NotifyMeSet);
  }

  sendToGtm() {
    gtm.pushToDataLayer({
      event: 'notify_me_set',
      page_position: this.data.pagePosition,
      previous_page_position: getPreviousPagePosition(),
      product_name: this.data.productName
    });
  }

  collectServerEvents() {
    this.serverEvents.push({
      data: { legacy_product_id: this.data.legacyId, os: this.data.os },
      type: 'notification_set'
    });
  }

  sendToBraze() {
    braze.requestPushPermission();
  }
}

export default SetNotificationEvent;
